import { tr } from '../lib/i18nHelpers';
import {
  buildFullStepper,
  buildStepper,
  NodeState,
  planNodeState,
  Stage,
  Stepper,
  tooltipFor,
} from '../lib/lifecycleStepper';
import Tooltip from './Tooltip';
import { Maximize2 } from 'lucide-react';

export type { Stage };

/** Localized stage name — shared with the drill-down modal's event arrows. */
export function stageLabel(s: Stage): string {
  const [en, zhHant, zhHans] = LABELS[s];
  return tr(en, zhHant, zhHans);
}

const LABELS: Record<Stage, [string, string, string]> = {
  uploaded: ['Uploaded', '已上傳', '已上传'],
  extracting: ['Extracting', '識別中', '识别中'],
  extracted: ['Extracted', '已識別', '已识别'],
  needs_review: ['Needs review', '待審核', '待审核'],
  reviewed: ['Reviewed', '已審核', '已审核'],
  booked: ['Booked (draft)', '已入賬（草稿）', '已入账（草稿）'],
  posted: ['Posted', '已過賬', '已过账'],
  reconciled: ['Reconciled', '已對賬', '已对账'],
  settled: ['Settled', '已結算', '已结算'],
  voided: ['Voided', '已作廢', '已作废'],
};

function label(s: Stage): string {
  return stageLabel(s);
}

export interface LifecycleStepperProps {
  type: 'document' | 'voucher';
  stage: Stage;
  stageSince?: string;
  variant?: 'compact' | 'full';
  /** Task 16: when given, a small expand icon opens the lineage drill-down modal. */
  onOpen?: () => void;
}

function dotCls(variant: 'compact' | 'full', stage: Stage, state: NodeState): string {
  const size = variant === 'compact' ? 'h-1.5 w-1.5' : 'h-2.5 w-2.5';
  // Terminal endcaps read as stoppers, not line dots.
  const shape = variant === 'full' && (stage === 'settled' || stage === 'voided')
    ? 'rounded-[3px]'
    : 'rounded-full';
  const color = state.current
    ? stage === 'voided'
      ? 'bg-red-500 ring-2 ring-red-200 dark:ring-red-900'
      : stage === 'settled'
        ? 'bg-green-600 ring-2 ring-green-200 dark:ring-green-900'
        : 'bg-blue-600 ring-2 ring-blue-200 dark:ring-blue-900'
    : state.past
      ? stage === 'voided'
        ? 'bg-red-500'
        : stage === 'settled'
          ? 'bg-green-600'
          : 'bg-blue-600'
      : 'bg-gray-300 dark:bg-gray-600';
  return `inline-block ${size} ${shape} ${color} cursor-default transition-colors`;
}

function tipFor(stage: Stage, state: NodeState, stageSince?: string): string {
  return tooltipFor({
    label: label(stage),
    hereLabel: tr('you are here', '目前狀態', '当前状态'),
    state,
    stageSince,
  });
}

function node(
  variant: 'compact' | 'full',
  s: Stage,
  state: NodeState,
  stageSince?: string
) {
  const dot = <span className={dotCls(variant, s, state)} />;
  const labelCls =
    variant === 'full'
      ? `text-[10px] ${
          state.current
            ? 'font-semibold text-blue-700 dark:text-blue-300'
            : state.past
              ? 'text-muted-foreground'
              : 'text-muted-foreground/50'
        }`
      : '';
  return (
    <Tooltip content={<span className="whitespace-nowrap text-xs">{tipFor(s, state, stageSince)}</span>}>
      <span className="inline-flex items-center gap-0.5">
        {variant === 'full' && <span className={labelCls}>{label(s)}</span>}
        {dot}
      </span>
    </Tooltip>
  );
}

function connectorCls(variant: 'compact' | 'full', past: boolean): string {
  const w = variant === 'compact' ? 'w-2' : 'w-4';
  return `inline-block ${w} h-px ${
    past ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
  }`;
}

/**
 * One lifecycle position indicator for every surface.
 * compact — dot row for list rows; EVERY dot hoverable -> stage name tooltip
 *           (past ✓, future dimmed, current "you are here" + since).
 * full    — labeled nodes for detail/review pages; needs_review is drawn as a
 *           detour branch off the main line; settled/voided replace the line's
 *           tail as an endcap when terminal.
 */
export default function LifecycleStepper({
  type,
  stage,
  stageSince,
  variant = 'compact',
  onOpen,
}: LifecycleStepperProps) {
  const plan: Stepper =
    variant === 'full' ? buildFullStepper(type, stage) : buildStepper(type, stage);

  // needs_review leaves the main line after this node (full variant only).
  const detourAt = plan.detour ? plan.main.indexOf(plan.detourAfter as Stage) + 1 : -1;

  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="img"
      aria-label={label(stage)}
    >
      {plan.main.map((s, i) => {
        const state: NodeState = planNodeState(plan, i);
        return (
          <span key={s} className="inline-flex items-center gap-0.5">
            {i > 0 && (
              <span
                className={`inline-block ${connectorCls(variant, i <= plan.idx)} align-middle`}
                aria-hidden
              />
            )}
            {variant === 'full' && i === detourAt && plan.detour && (
              <DetourBranch stage={plan.detour} stageSince={stageSince} />
            )}
            {node(variant, s, state, stageSince)}
          </span>
        );
      })}
      {plan.terminal && (
        <>
          <span
            className={`inline-block ${connectorCls(variant, false)} align-middle`}
            aria-hidden
          />
          {node(
            variant,
            plan.terminal,
            { past: false, current: true, future: false },
            stageSince
          )}
        </>
      )}
      {onOpen && (
        <Tooltip
          content={
            <span className="whitespace-nowrap text-xs">
              {tr('Open lifecycle drill-down', '開啟生命週期檢視', '打开生命周期视图')}
            </span>
          }
        >
          <button
            type="button"
            onClick={onOpen}
            aria-label={tr('Open lifecycle drill-down', '開啟生命週期檢視', '打开生命周期视图')}
            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-primary transition-colors cursor-pointer"
          >
            <Maximize2 className="h-3 w-3" aria-hidden />
          </button>
        </Tooltip>
      )}
    </span>
  );
}

/**
 * Full-variant detour branch: a diagonal stub dropping from the main line
 * down to the needs_review node, which sits below the row.
 */
function DetourBranch({
  stage: detourStage,
  stageSince,
}: {
  stage: Stage;
  stageSince?: string;
}) {
  // The branch is only rendered when the doc is actually at needs_review,
  // so from the viewer's perspective this node is always "current".
  const state: NodeState = { past: false, current: true, future: false };
  const tip = tipFor(detourStage, state, stageSince);
  return (
    <span
      className="relative inline-flex translate-y-2 flex-col items-center"
      role="img"
      aria-label={label(detourStage)}
    >
      <span
        className="h-[1px] w-3 origin-right -rotate-45 bg-blue-600"
        aria-hidden
      />
      <Tooltip content={<span className="whitespace-nowrap text-xs">{tip}</span>}>
        <span className="inline-flex items-center gap-0.5">
          <span className="text-[10px] font-semibold text-blue-700 dark:text-blue-300">
            {label(detourStage)}
          </span>
          <span className={dotCls('full', detourStage, state)} />
        </span>
      </Tooltip>
    </span>
  );
}
